/**
 * useGroupMembershipRequestNotifications Hook
 * Monitors membership requests to owned groups and sends notifications
 */

import { useEffect, useRef } from 'react';
import { useSettingsStore } from '@/stores/settings-store';
import { useAuthStore } from '@/stores/auth-store';
import { useNotifications } from './useNotifications';
import { apiGet } from '@/services/api-client';
import { hasStoredExpoPushToken } from '@/utils/expo-push-token';

interface MembershipRequest {
  id: string;
  groupId: string;
  groupName: string;
  userId: string;
  userName: string;
  status: string;
  createdAt: string;
}

interface Group {
  id: string;
  name: string;
  ownerId: string;
}

export function useGroupMembershipRequestNotifications() {
  const { sendNotification, permissionStatus } = useNotifications();
  const { notifications: notificationSettings } = useSettingsStore();
  const { accessToken, user, isAuthenticated } = useAuthStore();
  const seenRequestIdsRef = useRef<Set<string>>(new Set());
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !notificationSettings.groupMembershipRequests || !permissionStatus.granted || !accessToken || !user?.id) {
      return;
    }

    // Skip if user has push token (mobile app) - they get backend push notifications instead
    const hasPushToken = hasStoredExpoPushToken();
    if (hasPushToken) {
      console.log('[useGroupMembershipRequestNotifications] Skipping - user has push token, will get backend notifications');
      return;
    }

    // Poll for membership requests every 30 seconds
    const intervalId = setInterval(async () => {
      // Check for push token before each poll - stop polling if token exists
      const hasPushToken = hasStoredExpoPushToken();
      if (hasPushToken) {
        console.log('[useGroupMembershipRequestNotifications] Push token detected, stopping polling');
        clearInterval(intervalId);
        return;
      }

      try {
        // Get all groups owned by user
        const groupsRes = await apiGet<Group[]>('/groups');
        if (!groupsRes.success || !groupsRes.data) return;
        
        const ownedGroups = groupsRes.data.filter(g => g.ownerId === user.id);
        
        // On first run, just mark all requests as seen (don't notify for existing requests)
        if (!isInitializedRef.current) {
          for (const group of ownedGroups) {
            try {
              const requestsRes = await apiGet<{ requests: MembershipRequest[] }>(`/groups/${group.id}/membership-requests`);
              if (requestsRes.success && requestsRes.data) {
                requestsRes.data.requests
                  .filter(r => r.status === 'pending')
                  .forEach(r => seenRequestIdsRef.current.add(r.id));
              }
            } catch (error) {
              // Silent fail for individual group
            }
          }
          isInitializedRef.current = true;
          console.log('[useGroupMembershipRequestNotifications] Initialized with', seenRequestIdsRef.current.size, 'existing requests');
          return;
        }
        
        // Clean up requests from groups we no longer own
        const currentRequestIds = new Set<string>();
        
        // Check pending requests for each owned group
        for (const group of ownedGroups) {
          try {
            const requestsRes = await apiGet<{ requests: MembershipRequest[] }>(`/groups/${group.id}/membership-requests`);
            if (!requestsRes.success || !requestsRes.data) continue;
            
            const pendingRequests = requestsRes.data.requests.filter(r => r.status === 'pending');
            
            // Track current valid request IDs
            pendingRequests.forEach(r => currentRequestIds.add(r.id));
            
            // Check for new requests
            for (const request of pendingRequests) {
              if (!seenRequestIdsRef.current.has(request.id)) {
                seenRequestIdsRef.current.add(request.id);
                
                console.log('[useGroupMembershipRequestNotifications] New request detected for group:', group.name);
                
                // Send notification for new request
                await sendNotification({
                  title: 'Ny ansökan till grupp',
                  body: `${request.userName} vill gå med i ${group.name}`,
                  data: {
                    type: 'group-membership-request',
                    requestId: request.id,
                    groupId: group.id,
                    url: '/app/groups/notifications',
                  },
                });
              }
            }
          } catch (error) {
            console.error(`[useGroupMembershipRequestNotifications] Failed to check requests for group ${group.id}:`, error);
          }
        }
        
        // Only keep request IDs that still exist in our owned groups
        seenRequestIdsRef.current = new Set(
          Array.from(seenRequestIdsRef.current).filter(id => currentRequestIds.has(id))
        );
      } catch (error) {
        console.error('[useGroupMembershipRequestNotifications] Failed to check membership requests:', error);
      }
    }, 30 * 1000); // 30 seconds

    // Initial load - mark all existing requests as seen
    (async () => {
      try {
        const groupsRes = await apiGet<Group[]>('/groups');
        if (!groupsRes.success || !groupsRes.data) return;
        
        const ownedGroups = groupsRes.data.filter(g => g.ownerId === user.id);
        
        for (const group of ownedGroups) {
          try {
            const requestsRes = await apiGet<{ requests: MembershipRequest[] }>(`/groups/${group.id}/membership-requests`);
            if (requestsRes.success && requestsRes.data) {
              requestsRes.data.requests
                .filter(r => r.status === 'pending')
                .forEach(r => seenRequestIdsRef.current.add(r.id));
            }
          } catch (error) {
            // Silent fail for initial check
          }
        }
        isInitializedRef.current = true;
        console.log('[useGroupMembershipRequestNotifications] Initial load - marked', seenRequestIdsRef.current.size, 'existing requests as seen');
      } catch (error) {
        console.error('[useGroupMembershipRequestNotifications] Failed to initialize:', error);
      }
    })();

    return () => {
      clearInterval(intervalId);
      isInitializedRef.current = false;
    };
  }, [isAuthenticated, notificationSettings.groupMembershipRequests, permissionStatus.granted, accessToken, user?.id, sendNotification]);
}
